import React, { ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  width?: string;
  showCloseButton?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEsc?: boolean;
  description?: string;
  height?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  width = "600px",
  height = "250px",
  showCloseButton = true,
  closeOnClickOutside = true,
  closeOnEsc = true,
  description,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Create portal target - ensure it exists
  const portalTarget = typeof document !== "undefined" ? document.body : null;

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsMounted(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle ESC key press
  useEffect(() => {
    if (!closeOnEsc || !isMounted) return;

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => document.removeEventListener("keydown", handleEscKey);
  }, [closeOnEsc, isMounted, onClose]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isMounted) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isMounted]);

  // Handle click outside to close
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnClickOutside && event.target === event.currentTarget) {
      onClose();
    }
  };

  // Don't render if not mounted or no portal target
  if (!isMounted || !portalTarget) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center">
      {/* Overlay with fade effect */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-200 ease-in-out ${
          isAnimating ? "opacity-50" : "opacity-0"
        }`}
        onClick={handleOverlayClick}
      />

      {/* Modal Content with slide-up effect */}
      <div
        ref={modalRef}
        className={`relative z-10 bg-white rounded-2xl shadow-xl  flex flex-col overflow-hidden transition-all duration-200 ease-in-out ${
          isAnimating ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
        style={{ width, height }}
        onClick={(e) => e.stopPropagation()} // Prevent clicks inside modal from closing it
      >
        {/* Modal Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-start w-full px-6 py-2 border border-[#DFEAF2] border-rounded-2xl">
            <div>
              {title && (
                <h5 className="text-[20px] text-[#525062] font-medium w-full">
                  {title}
                </h5>
              )}
              {description && (
                <p className="text-center text-[#525062] text-[13px] mt-1 font-medium">
                  {description}
                </p>
              )}
            </div>

            {showCloseButton && (
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 focus:outline-none absolute top-4 right-4"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        {/* Modal Body */}
        <div className={`p-6 overflow-y-auto h-[${height}]`}>{children}</div>
      </div>
    </div>
  );

  // Render modal using Portal
  return createPortal(modalContent, portalTarget);
};

export default Modal;
